
import React from 'react';
import { SimulationConfig, EscapeBreakAnimation, EscapeGapAlignment, EscapeRotationMode, EscapeColorMode, RingCounterPosition } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { EscapeAudioConfig } from './EscapeAudioConfig';

interface EscapeSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const EscapeSettingsSection: React.FC<EscapeSettingsSectionProps> = ({ config, setConfig }) => {
  const breakAnims: EscapeBreakAnimation[] = [
    'EXPLOSION', 'RING_EXPAND', 'SHATTER', 'GLOW_FADE', 'STARDUST', 'VORTEX', 'PIXEL_BURST', 'SONIC_BOOM', 
    'FIREWORK', 'GHOST_ECHO', 'SHARD_STORM', 'NEBULA', 'DATA_STREAM', 'RIPPLE_WAVE', 'SHOCKWAVE', 'GLITCH_SQUARE', 
    'CONFETTI', 'ENERGY_ORBS', 'FRACTAL_GROWTH', 'RAINBOW_BURST', 'HEXAGON_GRID', 'PLASMA_WAVE', 'SMOKE_RING', 
    'LIGHTNING_STRIKE', 'SPIRAL_STARS', 'CRYSTAL_GROWTH', 'GALAXY_SWIRL', 'PHANTOM_RINGS', 'METEOR_SHOWER', 
    'QUANTUM_LEAP', 'FIRE_SPIRAL', 'AURORA_BOREALIS', 'DEEP_SEA_BUBBLES', 'TIME_WARP', 'PHYSICS_SHATTER', 
    'GRAVITY_CRUMBLE', 'EXPLOSIVE_SHARDS', 'FLOATING_DEBRIS', 'VORTEX_SHATTER', 'MOLTEN_DRIP', 'BOUNCY_CHUNKS', 
    'PIXEL_GRAVITY', 'RADIAL_SLICE', 'IMPLODE_EXPLODE', 'CHAIN_REACTION', 'SHRAPNEL', 'DEBRIS_CLOUD', 
    'ASTEROID_FIELD', 'VOLCANIC_BURST', 'SUPERNOVA_FRAG', 'BLACK_HOLE_EAT', 'NEBULA_DRIFT', 'COMET_TAIL', 'PRISM_SPLIT',
    'GRAVITY_DUST'
  ];

  const hudPositions: RingCounterPosition[] = ['CENTER', 'TOP_CENTER', 'BOTTOM_CENTER', 'FOLLOW_BALL', 'NONE'];
  const gapAlignments: EscapeGapAlignment[] = ['ALIGNED', 'RANDOM', 'SPIRAL', 'FIXED_SIZE', 'ONE_SIDE', 'STAGGERED'];
  const rotationModes: EscapeRotationMode[] = ['ALTERNATING', 'CLOCKWISE', 'COUNTER_CLOCKWISE', 'NONE'];
  const fonts = [
      'Inter, sans-serif', 'Arial, sans-serif', 'Courier New, monospace', 
      'Times New Roman, serif', 'Georgia, serif', 'Verdana, sans-serif', 
      'Impact, sans-serif', 'Comic Sans MS, cursive', 'Trebuchet MS, sans-serif',
      'Lucida Console, monospace', 'Tahoma, sans-serif', 'Brush Script MT, cursive'
  ];
  const weights = ["100", "200", "300", "400", "500", "600", "700", "800", "900"];

  const ColorInput = ({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) => (
    <ControlGroup label={label}>
        <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
            <input type="color" value={value} onChange={e => onChange(e.target.value)} className="flex-1 h-8 opacity-0 absolute cursor-pointer" />
            <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`flex-1 px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
        </div>
    </ControlGroup>
  );

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/20 mb-4">
      <SectionLabel icon="fa-door-open" darkMode={config.darkMode}>Escape Engine</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between flex-col gap-2 border border-blue-500/20 p-3 rounded-2xl bg-blue-500/5">
          <span className="text-[11px] font-black uppercase text-center text-blue-400">Static Mode</span>
          <Toggle active={config.escapeStaticMode} onClick={() => setConfig(p => ({...p, escapeStaticMode: !p.escapeStaticMode}))} color="bg-blue-600" />
        </div>
        <div className="flex items-center justify-between flex-col gap-2 border border-red-500/20 p-3 rounded-2xl bg-red-500/5">
          <span className="text-[11px] font-black uppercase text-center text-red-400">Timer Mode</span>
          <Toggle active={config.escapeTimeChallengeMode} onClick={() => setConfig(p => ({...p, escapeTimeChallengeMode: !p.escapeTimeChallengeMode}))} color="bg-red-500" />
        </div>
      </div>

      <EscapeAudioConfig config={config} setConfig={setConfig} />

      {/* NEW HUD DISPLAY SECTION */}
      <SectionLabel icon="fa-bullseye" darkMode={config.darkMode}>Heads-Up Display (HUD)</SectionLabel>
      <div className="flex flex-col gap-3 p-3 rounded-2xl bg-black/5 border border-white/5">
          <div className="grid grid-cols-2 gap-3">
              <ControlGroup label="Position">
                <select value={config.ringCounterPosition} onChange={e => setConfig(p => ({...p, ringCounterPosition: e.target.value as RingCounterPosition}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                  {hudPositions.map(pos => <option key={pos} value={pos}>{pos.replace('_', ' ')}</option>)}
                </select>
              </ControlGroup>
              <ControlGroup label="Font Family">
                <select value={config.ringCounterFont} onChange={e => setConfig(p => ({...p, ringCounterFont: e.target.value}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                  {fonts.map(f => <option key={f} value={f}>{f.split(',')[0]}</option>)}
                </select>
              </ControlGroup>
          </div>

          <div className="flex items-center justify-between border border-white/10 p-2 rounded-xl bg-white/5">
             <span className="text-[9px] font-black uppercase opacity-60">Reverse Counter (Count Up)</span>
             <Toggle active={config.ringCounterReverse} onClick={() => setConfig(p => ({...p, ringCounterReverse: !p.ringCounterReverse}))} />
          </div>

          <div className="grid grid-cols-2 gap-3">
              <ControlGroup label="Weight">
                <select value={config.ringCounterFontWeight} onChange={e => setConfig(p => ({...p, ringCounterFontWeight: e.target.value}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
                  {weights.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </ControlGroup>
              <SmartInput label="Font Size" min={10} max={600} value={config.ringCounterFontSize} onChange={v => setConfig(p => ({...p, ringCounterFontSize: v}))} darkMode={config.darkMode} />
          </div>

          {/* FILL COLOR */}
          <div className="flex items-center justify-between mt-2">
              <span className="text-[9px] font-black uppercase opacity-60">Rainbow Fill</span>
              <Toggle active={config.ringCounterUseRGB} onClick={() => setConfig(p => ({...p, ringCounterUseRGB: !p.ringCounterUseRGB}))} color="bg-pink-500" />
          </div>
          {!config.ringCounterUseRGB && (
              <ColorInput label="Fill Color" value={config.ringCounterColor} onChange={v => setConfig(p => ({...p, ringCounterColor: v}))} />
          )}
          
          <div className="h-px bg-white/10 my-1" />
          <span className="text-[8px] font-black uppercase opacity-40">Borders & Shadows</span>
          
          <div className="grid grid-cols-2 gap-3">
              <SmartInput label="Border Width" min={0} max={20} value={config.ringCounterStrokeWidth} onChange={v => setConfig(p => ({...p, ringCounterStrokeWidth: v}))} darkMode={config.darkMode} />
              <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.ringCounterOpacity} onChange={v => setConfig(p => ({...p, ringCounterOpacity: v}))} darkMode={config.darkMode} />
          </div>
          
          {/* BORDER COLOR */}
          <div className="flex items-center justify-between mt-1">
              <span className="text-[9px] font-black uppercase opacity-60">Rainbow Border</span>
              <Toggle active={config.ringCounterUseRGBBorder} onClick={() => setConfig(p => ({...p, ringCounterUseRGBBorder: !p.ringCounterUseRGBBorder}))} color="bg-pink-500" />
          </div>
          {!config.ringCounterUseRGBBorder && (
             <ColorInput label="Border Color" value={config.ringCounterStrokeColor} onChange={v => setConfig(p => ({...p, ringCounterStrokeColor: v}))} />
          )}

          <div className="grid grid-cols-2 gap-3 mt-1">
              <SmartInput label="Shadow Blur" min={0} max={50} value={config.ringCounterShadowBlur} onChange={v => setConfig(p => ({...p, ringCounterShadowBlur: v}))} darkMode={config.darkMode} />
              {config.ringCounterShadowBlur > 0 && (
                  <div className="relative pt-4"> 
                      <input type="color" value={config.ringCounterShadowColor} onChange={e => setConfig(p => ({...p, ringCounterShadowColor: e.target.value}))} className="w-full h-6 rounded cursor-pointer" />
                  </div>
              )}
          </div>

          {config.ringCounterPosition === 'FOLLOW_BALL' && (
            <SmartInput label="Follow Offset" min={-200} max={200} value={config.ringCounterOffset} onChange={v => setConfig(p => ({...p, ringCounterOffset: v}))} darkMode={config.darkMode} />
          )}
      </div>

      <SectionLabel icon="fa-palette" darkMode={config.darkMode}>Appearance & Mechanics</SectionLabel>
      
      {/* NEW: GAP SIDE GUARDS TOGGLE */}
      <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl mb-3 bg-blue-500/5">
         <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase text-blue-500">Gap Side Guards</span>
            <span className="text-[8px] opacity-60">White borders that guide ball into gap</span>
         </div>
         <Toggle active={config.escapeSideGuards} onClick={() => setConfig(p => ({...p, escapeSideGuards: !p.escapeSideGuards}))} color="bg-blue-600" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ControlGroup label="Gap Align">
          <select value={config.escapeGapAlignment} onChange={e => setConfig(p => ({...p, escapeGapAlignment: e.target.value as EscapeGapAlignment}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
            {gapAlignments.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
          </select>
        </ControlGroup>
        <ControlGroup label="Rotation">
          <select value={config.escapeRotationMode} onChange={e => setConfig(p => ({...p, escapeRotationMode: e.target.value as EscapeRotationMode}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
            {rotationModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
          </select>
        </ControlGroup>
      </div>

      <SmartInput label="Ring Count" min={1} max={1000} value={config.escapeRingCount} onChange={v => setConfig(p => ({...p, escapeRingCount: v}))} darkMode={config.darkMode} />
      <SmartInput label="Gap Width" min={5} max={90} value={config.escapeGapSize} onChange={v => setConfig(p => ({...p, escapeGapSize: v}))} darkMode={config.darkMode} />
      <SmartInput label="Rot Speed" min={0} max={10} step={0.1} value={config.escapeRotationSpeed} onChange={v => setConfig(p => ({...p, escapeRotationSpeed: v}))} darkMode={config.darkMode} />
      
      <ControlGroup label="Break Anim">
        <select value={config.escapeBreakAnimation} onChange={e => setConfig(p => ({...p, escapeBreakAnimation: e.target.value as EscapeBreakAnimation}))} className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}>
          {breakAnims.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
        </select>
      </ControlGroup>
      
      <SmartInput label="Ring Spacing" min={1} max={100} value={config.escapeRingSpacing} onChange={v => setConfig(p => ({...p, escapeRingSpacing: v}))} darkMode={config.darkMode} />
      
      {/* DYNAMIC ACCELERATION SETTINGS */}
      <div className="flex items-center justify-between border border-yellow-500/10 p-2 rounded-xl mt-3 bg-yellow-500/5">
         <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase text-yellow-500">Combo Acceleration</span>
            <span className="text-[8px] opacity-60">Speed up when breaking rings fast</span>
         </div>
         <Toggle active={config.escapeAccelerationEnabled} onClick={() => setConfig(p => ({...p, escapeAccelerationEnabled: !p.escapeAccelerationEnabled}))} color="bg-yellow-600" />
      </div>
      
      {config.escapeAccelerationEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-2 p-2 bg-yellow-500/5 rounded-xl border border-yellow-500/10">
              <SmartInput label="Boost Per Ring" min={0.01} max={1.0} step={0.01} value={config.escapeAccelerationMultiplier} onChange={v => setConfig(p => ({...p, escapeAccelerationMultiplier: v}))} darkMode={config.darkMode} />
              <SmartInput label="Decay Rate" min={0.001} max={0.2} step={0.001} value={config.escapeAccelerationDecay} onChange={v => setConfig(p => ({...p, escapeAccelerationDecay: v}))} darkMode={config.darkMode} />
              <SmartInput label="Max Boost" min={0.1} max={10.0} step={0.1} value={config.escapeAccelerationMax} onChange={v => setConfig(p => ({...p, escapeAccelerationMax: v}))} darkMode={config.darkMode} />
          </div>
      )}
    </div>
  );
};
